import React, { useEffect, useRef, useState } from 'react';

const HOVER_SCALE_SELECTOR = '[data-cursor-hover="project"], [data-cursor-hover="cta"]';

const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

const CursorImpl = () => {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isScaleHover, setIsScaleHover] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const mouse = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const rafId = useRef<number>(0);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onMatch = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener('change', onMatch);
    return () => mq.removeEventListener('change', onMatch);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX - 3}px, ${e.clientY - 3}px)`;
      }
    };

    const onMouseDown = () => setIsClicking(true);
    const onMouseUp = () => setIsClicking(false);

    const onHoverStart = (e: Event) => {
      const target = e.target as Element;
      if (target.closest('a, button, [role="button"], input, textarea, select')) {
        setIsHovering(true);
      }
      if (target.closest(HOVER_SCALE_SELECTOR)) {
        setIsScaleHover(true);
      }
    };

    const onHoverEnd = (e: Event) => {
      setIsHovering(false);
      const rel = (e as MouseEvent).relatedTarget as Element | null;
      if (!rel?.closest?.(HOVER_SCALE_SELECTOR)) {
        setIsScaleHover(false);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mouseover', onHoverStart);
    document.addEventListener('mouseout', onHoverEnd);

    function animate() {
      const lerp = 0.12;
      ringPos.current.x += (mouse.current.x - ringPos.current.x) * lerp;
      ringPos.current.y += (mouse.current.y - ringPos.current.y) * lerp;

      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ringPos.current.x - 18}px, ${ringPos.current.y - 18}px)`;
      }

      rafId.current = requestAnimationFrame(animate);
    }

    rafId.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mouseover', onHoverStart);
      document.removeEventListener('mouseout', onHoverEnd);
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <>
      {/* Dot */}
      <div
        ref={dotRef}
        className="cursor-dot"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: isClicking ? '#fff' : 'rgb(52, 211, 153)',
          pointerEvents: 'none',
          zIndex: 99998,
          willChange: 'transform',
          transition: 'background-color 0.15s',
        }}
      />
      {/* Ring */}
      <div
        ref={ringRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: `1.5px solid ${isHovering ? 'rgba(0,200,255,0.8)' : 'rgba(0,200,255,0.35)'}`,
          pointerEvents: 'none',
          zIndex: 99997,
          willChange: 'transform',
          transform: reduceMotion ? 'scale(1)' : isScaleHover ? 'scale(2)' : isHovering ? 'scale(1.6)' : 'scale(1)',
          transition: 'border-color 0.2s, opacity 0.2s',
          opacity: isClicking ? 0.4 : 1,
        }}
      />
    </>
  );
};

const CustomCursor = () => isTouchDevice ? null : <CursorImpl />;

export default CustomCursor;
